import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { AudioFile, ProcessingJob } from '../types'

interface AppState {
  files: AudioFile[]
  jobs: ProcessingJob[]
  selectedInputFileId: string | null
  selectedReferenceFileId: string | null
  activeJobIds: string[]

  setFiles: (files: AudioFile[]) => void
  upsertFile: (file: AudioFile) => void
  removeFile: (id: string) => void

  setJobs: (jobs: ProcessingJob[]) => void
  upsertJob: (job: ProcessingJob) => void
  removeJob: (id: string) => void

  setSelectedInputFileId: (id: string | null) => void
  setSelectedReferenceFileId: (id: string | null) => void

  addActiveJobId: (id: string) => void
  removeActiveJobId: (id: string) => void

  resetSessionState: () => void
}

const byCreatedAtDesc = <T extends { created_at: string }>(items: T[]) =>
  [...items].sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      files: [],
      jobs: [],
      selectedInputFileId: null,
      selectedReferenceFileId: null,
      activeJobIds: [],

      setFiles: (files) => set({ files: byCreatedAtDesc(files) }),
      upsertFile: (file) =>
        set((state) => {
          const existing = state.files.filter((item) => item.id !== file.id)
          const next = byCreatedAtDesc([file, ...existing])
          const shouldAutoSelectInput =
            file.role === 'input' &&
            (!state.selectedInputFileId ||
              !next.some((item) => item.id === state.selectedInputFileId))
          const shouldAutoSelectReference =
            file.role === 'reference' &&
            (!state.selectedReferenceFileId ||
              !next.some((item) => item.id === state.selectedReferenceFileId))
          return {
            files: next,
            selectedInputFileId: shouldAutoSelectInput
              ? file.id
              : state.selectedInputFileId,
            selectedReferenceFileId: shouldAutoSelectReference
              ? file.id
              : state.selectedReferenceFileId,
          }
        }),
      removeFile: (id) =>
        set((state) => ({
          files: state.files.filter((item) => item.id !== id),
          selectedInputFileId:
            state.selectedInputFileId === id ? null : state.selectedInputFileId,
          selectedReferenceFileId:
            state.selectedReferenceFileId === id ? null : state.selectedReferenceFileId,
        })),

      setJobs: (jobs) => set({ jobs: byCreatedAtDesc(jobs) }),
      upsertJob: (job) =>
        set((state) => {
          const nextJobs = byCreatedAtDesc([
            job,
            ...state.jobs.filter((item) => item.id !== job.id),
          ])
          const isTerminal = job.status === 'completed' || job.status === 'failed'
          return {
            jobs: nextJobs,
            activeJobIds: isTerminal
              ? state.activeJobIds.filter((id) => id !== job.id)
              : Array.from(new Set([job.id, ...state.activeJobIds])),
          }
        }),
      removeJob: (id) =>
        set((state) => ({
          jobs: state.jobs.filter((item) => item.id !== id),
          activeJobIds: state.activeJobIds.filter((item) => item !== id),
        })),

      setSelectedInputFileId: (id) => set({ selectedInputFileId: id }),
      setSelectedReferenceFileId: (id) => set({ selectedReferenceFileId: id }),

      addActiveJobId: (id) =>
        set((state) => ({ activeJobIds: Array.from(new Set([id, ...state.activeJobIds])) })),
      removeActiveJobId: (id) =>
        set((state) => ({ activeJobIds: state.activeJobIds.filter((item) => item !== id) })),

      resetSessionState: () =>
        set({
          files: [],
          jobs: [],
          selectedInputFileId: null,
          selectedReferenceFileId: null,
          activeJobIds: [],
        }),
    }),
    {
      name: 'ai-producer-studio-state',
      partialize: (state) => ({
        selectedInputFileId: state.selectedInputFileId,
        selectedReferenceFileId: state.selectedReferenceFileId,
        activeJobIds: state.activeJobIds,
      }),
    }
  )
)
